import {
	Box,
	Button,
	Flex,
	HStack,
	IconButton,
	Input,
	Select,
	Text,
	Tooltip,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

import { BUTTON_TEXT_MAX } from './utils/metaWhatsappTemplate';

/**
 * The buttons a template offers, if any.
 *
 * ★ THREE KINDS, AND THEY ARE NOT INTERCHANGEABLE. A quick reply sends its own
 * label back to us as an ordinary inbound message — which is how an opt-out
 * button works, and why it costs nothing. A link and a call button leave
 * WhatsApp entirely and tell us nothing at all. People reach for "button"
 * meaning one of the three without noticing the difference, so each says what
 * it does rather than just naming itself.
 *
 * ★ THE LINK VARIABLE IS A SUFFIX. Meta builds the final address by appending
 * the parameter to a fixed prefix, so `…/{{1}}/track` cannot exist. The field
 * says so before the submit, because Meta's own refusal is a bare code 100 that
 * names neither the button nor the reason.
 */

const TYPES = [
	{ value: 'QUICK_REPLY', label: 'Quick reply', help: 'The customer taps it and their reply comes back to you here.' },
	{ value: 'URL', label: 'Open a link', help: 'Takes them to a web page. You will not see whether they tapped it.' },
	{ value: 'PHONE_NUMBER', label: 'Call a number', help: 'Starts a phone call. You will not see whether they tapped it.' },
];

const blank = (type) => ({ type, text: '', url: '', phone: '', urlExample: '' });

/** A link whose address ends in a variable needs a sample of the whole thing. */
const isDynamic = (url) => /\{\{\s*\d+\s*\}\}\s*$/.test(String(url ?? ''));

export default function TemplateButtons({ value = [], onChange, ui }) {
	const buttons = Array.isArray(value) ? value : [];

	const set = (index, patch) =>
		onChange(buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)));
	const add = (type) => onChange([...buttons, blank(type)]);
	const remove = (index) => onChange(buttons.filter((_, i) => i !== index));

	return (
		<Box>
			<Flex justify='space-between' align='center' mb='2' gap='3' wrap='wrap'>
				<Text fontWeight='medium'>Buttons (optional)</Text>
				<HStack spacing='2'>
					{TYPES.map((type) => (
						<Tooltip key={type.value} label={type.help} hasArrow placement='top'>
							<Button size='xs' variant='outline' leftIcon={<FiPlus />} onClick={() => add(type.value)}>
								{type.label}
							</Button>
						</Tooltip>
					))}
				</HStack>
			</Flex>

			{!buttons.length ? (
				<Text fontSize='sm' color={ui?.textMuted ?? 'gray.500'}>
					Most templates have none. A quick reply is the usual way to offer an opt-out.
				</Text>
			) : (
				buttons.map((button, index) => (
					// eslint-disable-next-line react/no-array-index-key
					<Box key={index} borderWidth='1px' borderRadius='md' p='3' mb='2'>
						<Flex gap='2' align='flex-start' wrap='wrap'>
							<Select
								size='sm'
								maxW='170px'
								value={button.type}
								onChange={(e) => set(index, { type: e.target.value })}
							>
								{TYPES.map((t) => (
									<option key={t.value} value={t.value}>
										{t.label}
									</option>
								))}
							</Select>

							<Input
								size='sm'
								flex='1 1 160px'
								placeholder='Button label'
								maxLength={BUTTON_TEXT_MAX}
								value={button.text ?? ''}
								onChange={(e) => set(index, { text: e.target.value })}
							/>

							{button.type === 'URL' && (
								<Input
									size='sm'
									flex='1 1 220px'
									placeholder='https://example.com/track/{{1}}'
									value={button.url ?? ''}
									onChange={(e) => set(index, { url: e.target.value })}
								/>
							)}

							{button.type === 'PHONE_NUMBER' && (
								<Input
									size='sm'
									flex='1 1 180px'
									placeholder='+971500000000'
									value={button.phone ?? ''}
									onChange={(e) => set(index, { phone: e.target.value })}
								/>
							)}

							<IconButton
								size='sm'
								variant='ghost'
								aria-label='Remove this button'
								icon={<FiTrash2 />}
								onClick={() => remove(index)}
							/>
						</Flex>

						{/* Only a link that ENDS in a variable needs an example, and the
						    example is the finished address — Meta checks that it
						    resolves, not that the parameter looks plausible. */}
						{button.type === 'URL' && isDynamic(button.url) && (
							<Input
								size='sm'
								mt='2'
								placeholder='Example of the finished link, e.g. https://example.com/track/ab12'
								value={button.urlExample ?? ''}
								onChange={(e) => set(index, { urlExample: e.target.value })}
							/>
						)}
					</Box>
				))
			)}
		</Box>
	);
}
